import React, { useState, useEffect } from "react";
import "./Home.css";
import TechBackground from "./TechBackground";
import Projects from "./Projects";
import Connect from './Connect';

const ProjectGallery: React.FC<{ images: string[]; alt: string }> = ({ images, alt }) => {
  const [index, setIndex] = useState(0);
  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + images.length) % images.length);
  };
  const current = images[index];
  const isWorkflow = current.toLowerCase().includes("image1");
  return (
    <div style={{ position: "relative" }}>
      <img src={current} alt={alt} loading="lazy" decoding="async" />
      {images.length > 1 && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px" }}>
          <button className="btn btn-outline" onClick={() => go(-1)} aria-label="Previous image">‹</button>
          <button className="btn btn-outline" onClick={() => go(1)} aria-label="Next image">›</button>
        </div>
      )}
      {isWorkflow && (
        <div style={{ marginTop: 6, fontSize: "0.85rem", color: "var(--text-dim)" }}>Workflow of app</div>
      )}
    </div>
  );
};

const ImagePopup: React.FC<{ image: string; alt: string; isOpen: boolean; onClose: () => void }> = ({ image, alt, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="image-popup-overlay" onClick={onClose}>
      <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="image-popup-close" onClick={onClose} aria-label="Close popup">×</button>
        <img src={image} alt={alt} className="image-popup-image" />
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [popupImage, setPopupImage] = useState<string | null>(null);
  const [popupAlt, setPopupAlt] = useState<string>('');

  const typingTexts = [
    'solving hard problems',
    'creating UI/UX',
    'building digital experiences'
  ];

  useEffect(() => {
    const currentFullText = typingTexts[currentTextIndex];
    
    if (isDeleting) {
      // Deleting text
      if (currentText.length > 0) {
        const timeout = setTimeout(() => {
          setCurrentText(currentText.slice(0, -1));
        }, 50);
        return () => clearTimeout(timeout);
      } else {
        // Move to next text
        setIsDeleting(false);
        setCurrentTextIndex((prev: number) => (prev + 1) % typingTexts.length);
      }
    } else {
      // Typing text
      if (currentText.length < currentFullText.length) {
        const timeout = setTimeout(() => {
          setCurrentText(currentFullText.slice(0, currentText.length + 1));
        }, 100);
        return () => clearTimeout(timeout);
      } else {
        // Wait before starting to delete
        const timeout = setTimeout(() => {
          setIsDeleting(true);
        }, 2000);
        return () => clearTimeout(timeout);
      }
    }
  }, [currentText, currentTextIndex, isDeleting, typingTexts]);

  const openImagePopup = (image: string, alt: string) => {
    setPopupImage(image);
    setPopupAlt(alt);
  };

  const closeImagePopup = () => {
    setPopupImage(null);
    setPopupAlt('');
  };


  const projects = [
    // {
    //   title: "E-Commerce Platform",
    //   description: "Full-stack e-commerce solution with React, Node.js, and PostgreSQL",
    //   tech: ["React", "Node.js", "PostgreSQL", "Stripe"],
    //   image: "https://via.placeholder.com/300x200/667eea/ffffff?text=E-Commerce",
    // },
    // {
    //   title: "Task Management App",
    //   description: "Collaborative task management with real-time updates",
    //   tech: ["React", "Socket.io", "MongoDB", "Express"],
    //   image: "https://via.placeholder.com/300x200/764ba2/ffffff?text=Task+App",
    // },
    // {
    //   title: "Portfolio Website",
    //   description: "Modern portfolio website with smooth animations",
    //   tech: ["React", "TypeScript", "CSS3", "Framer Motion"],
    //   image: "https://via.placeholder.com/300x200/48bb78/ffffff?text=Portfolio",
    // },
    {
      title: "PINAC Workspace",
      description: "Privacy-first desktop AI chat app built with Electron + React UI and a powerful Python backend. Choose between local (Ollama) and cloud (Pinac-Cloud) models; offline-first with local chat history and optional local RAG, real-time web search, secure auth via Firebase, modern desktop UX.",
      tech: [
        "Electron",
        "React",
        "Python",
        "Firebase Auth",
        "RAG",
        "Ollama",
      ],
      image: "/pinac/image1.png",
      imageAlt: "PINAC Workspace screenshot",
      gallery: ["/pinac/image1.png", "/pinac/image2.png"],
      repo: "https://github.com/pinacai/PINAC_Workspace",
      website: "https://pinacworkspace.vercel.app/"
    },
    {
      title: "Hamro Class",
      description: "Syllabus tracker for classes to solve traditional syllabus completion problems. Tailored UIs for teacher, student, and college administration to plan, track, and review progress.",
      tech: ["React", "MongoDB", "Express", "Node.js"],
      image: "/hamroclass/image3.png",
      imageAlt: "Hamro Class preview",
      gallery: ["/hamroclass/image3.png", "/hamroclass/image1.png", "/hamroclass/image2.png", "/hamroclass/image.png"],
      repoFrontend: "https://github.com/Dosty-Team/hamroclass",
      repoBackend: "https://github.com/Dosty-Team/hamroclass-backend",
      website:"https://drive.google.com/file/d/1U6JE3-jZOyHJRjcvqgVMB1ZyNhn630Th/view?usp=drive_link "
    },
    {
      title: "Information Management System",
      description: "Solution for businesses. \n\n Features include dashboard analytics, product management, category organization, sales tracking, sales reporting, sales prediction, user management, and shareholder distribution. Built to address traditional inventory management challenges by streamlining processes, enhancing accuracy, and providing agile solutions for modern business environments.",
      tech: ["React", "MongoDB", "Express", "Node.js"],
      image: "/IMS/shareholder_page.png",
      imageAlt: "IMS Dashboard Management",
      gallery: [
        "/IMS/categorypage.png",
        "/IMS/dashboard.png",
        "/IMS/productpage.png",
        "/IMS/salespage.png",
        "/IMS/salesprediction.png",
        "/IMS/salesreport.png",
        "/IMS/shareholder_page.png",
        "/IMS/usermanage.png"
      ],repoFrontend: "https://github.com/Dosty-Team/Inventory-Management-System/tree/frontend-v3",
      repoBackend: "https://github.com/Dosty-Team/Inventory-Management-System/tree/backendpart2"
    }
  ];

  return (
    <div className="home">
      {/* Hero Section with TechBackground */}
      <section className="hero" aria-label="Hero">
        <div className="hero-bg">
          <TechBackground />
          <div className="hero-content">
            <h1 className="hero-title">
              Hi, I&apos;m <span className="highlight">Nirajan Bhattarai</span>
            </h1>

            <p className="hero-subtitle">
              I&apos;m passionate about{" "}
              <span className="typing">
                <span className="typing-text">{currentText}</span><span className="caret">|</span>
              </span>
            </p>

            <p className="hero-description">
              I love exploring modern web stacks and crafting reliable, scalable systems.
              My curiosity drives continuous learning and building delightful developer and user
              experiences.
            </p>

            <div className="hero-buttons">
              {/* <a className="btn btn-primary" href="#" aria-label="Download Resume">
                <span>Download Resume</span>
                <span className="btn-icon" aria-hidden>☁️</span>
              </a> */}
              <a className="btn btn-secondary" href="#contact">
                <span>Connect With Me →</span>
              </a>
            </div>

            <div className="scroll-indicator" aria-hidden="true">
              <span>Scroll to explore</span>
              <div className="scroll-arrow">⌄</div>
            </div>
          </div>
        </div>
      </section>

      {/* Certificates Section */}
      <section className="certificates-section" id="certificates" aria-label="Professional Certifications">
        <div className="container">
          <h2 className="section-title">Professional Certifications</h2>
          <div className="certificates-grid">
            <div className="certificate-card">
              <div className="certificate-header">
                <div className="certificate-icon">🎓</div>
                <div className="certificate-title-section">
                  <h3 className="certificate-title">DevOps Course</h3>
                  <div className="certificate-instructor">Instructor: Imran Teli</div>
                </div>
              </div>
              <div className="certificate-content">
                <div className="certificate-image">
                  <img src="/certificate/devops.jpg" alt="DevOps Course Certificate" onClick={() => openImagePopup("/certificate/devops.jpg", "DevOps Course Certificate")} />
                </div>
                <div className="certificate-hours">55+ hours</div>
                <div className="certificate-focus">
                  <strong>Focused on:</strong> Advanced DevOps course and efficient scaling.
                </div>
                <div className="certificate-meta">
                  <span className="certificate-platform">Udemy</span>
                  <span className="certificate-date">2024-2025</span>
                </div>
                <a 
                  href="https://www.udemy.com/certificate/UC-e4415c41-e6d2-4032-9f67-959dea9ae5de/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="certificate-link"
                >
                  View Certificate →
                </a>
              </div>
            </div>

            <div className="certificate-card">
              <div className="certificate-header">
                <div className="certificate-icon">⚡</div>
                <div className="certificate-title-section">
                  <h3 className="certificate-title">Troubleshooting Backend Performance</h3>
                  <div className="certificate-instructor">Instructor: Hussein Nasser</div>
                </div>
              </div>
              <div className="certificate-content">
                <div className="certificate-image">
                  <img src="/certificate/backend_performance.jpg" alt="Backend Performance Certificate" onClick={() => openImagePopup("/certificate/backend_performance.jpg", "Backend Performance Certificate")} />
                </div>
                <div className="certificate-hours">10.5 hours</div>
                <div className="certificate-focus">
                  <strong>Focused on:</strong> Debugging performance in client side & server side, tweaking network level settings in docker container, debugging reverse proxies.
                </div>
                <div className="certificate-meta">
                  <span className="certificate-platform">Udemy</span>
                  <span className="certificate-date">2024-2025</span>
                </div>
                <a 
                  href="https://www.udemy.com/certificate/UC-3dd4af6e-609f-40ec-a5b7-e4d907a27ab3/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="certificate-link"
                >
                  View Certificate →
                </a>
              </div>
            </div>

            <div className="certificate-card">
              <div className="certificate-header">
                <div className="certificate-icon">🌐</div>
                <div className="certificate-title-section">
                  <h3 className="certificate-title">Fundamentals of Network Engineering</h3>
                  <div className="certificate-instructor">Instructor: Hussein Nasser</div>
                </div>
              </div>
              <div className="certificate-content">
                <div className="certificate-image">
                  <img src="/certificate/network_certificate.jpg" alt="Network Engineering Certificate" onClick={() => openImagePopup("/certificate/network_certificate.jpg", "Network Engineering Certificate")} />
                </div>
                <div className="certificate-hours">18.5 hours</div>
                <div className="certificate-focus">
                  <strong>Focused on:</strong> Creating own webserver in Go, network proxies, network architectures, websockets, reverse proxy.
                </div>
                <div className="certificate-meta">
                  <span className="certificate-platform">Udemy</span>
                  <span className="certificate-date">2024-2025</span>
                </div>
                <a 
                  href="https://www.udemy.com/certificate/UC-c0f4d082-aac4-4ef4-a0f6-bc296d0430df/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="certificate-link"
                >
                  View Certificate →
                </a>
              </div>
            </div>

            <div className="certificate-card">
              <div className="certificate-header">
                <div className="certificate-icon">💻</div>
                <div className="certificate-title-section">
                  <h3 className="certificate-title">Frontend Course</h3>
                  <div className="certificate-instructor">Instructor: Yubraj Rai</div>
                </div>
              </div>
              <div className="certificate-content">
                <div className="certificate-image">
                  <img src="/frontend.jpg" alt="Frontend Course Certificate" onClick={() => openImagePopup("/frontend.jpg", "Frontend Course Certificate")} />
                </div>
                <div className="certificate-hours">45 hours</div>
                <div className="certificate-focus">
                  <strong>Issued from:</strong> School of Information Technologies<br/>
                  <strong>Affiliated with:</strong> Pearson<br/>
                  <strong>Certificate No:</strong> SIT91012
                </div>
                <div className="certificate-meta">
                  <span className="certificate-platform">School of IT</span>
                  <span className="certificate-date">June 4, 2022</span>
                </div>
                <div className="certificate-duration">
                  March 1, 2022 to May 30, 2022
                </div>
              </div>
            </div>

            <div className="certificate-card">
              <div className="certificate-header">
                <div className="certificate-icon">📊</div>
                <div className="certificate-title-section">
                  <h3 className="certificate-title">Data Science with Python</h3>
                  <div className="certificate-instructor">Broadway Infosys</div>
                </div>
              </div>
              <div className="certificate-content">
                <div className="certificate-image">
                  <img src="/datascience.jpg" alt="Data Science Certificate" onClick={() => openImagePopup("/datascience.jpg", "Data Science Certificate")} />
                </div>
                <div className="certificate-hours">45 hours</div>
                <div className="certificate-focus">
                  <strong>Issued from:</strong> Broadway Infosys<br/>
                  <strong>Certificate No:</strong> B20738
                </div>
                <div className="certificate-meta">
                  <span className="certificate-platform">Broadway Infosys</span>
                  <span className="certificate-date">Nov 11, 2024</span>
                </div>
                <div className="certificate-duration">
                  August 21, 2024 to November 11, 2024
                </div>
                <a 
                  href="https://broadwayinfosys.com/certificate-verification-response" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="certificate-link"
                >
                  Verify Certificate →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Skills Section */}
      <section className="skills-section" id="skills" aria-label="Technical Skills">
        <div className="container">
          <div className="skills-header">
            <h2 className="section-title">
              Technical <span className="highlight">Skills</span>
            </h2>
            <p className="skills-description">
              I've gained proficiency in various technologies throughout my career. Here are the key tools and frameworks I use to build exceptional products.
            </p>
          </div>
          
          <div className="skills-grid">
            {/* Mobile */}
            <div className="skill-category">
              <div className="skill-category-header">
                <div className="skill-icon">📱</div>
                <h3>Mobile / Web</h3>
              </div>
              <div className="skill-tags">
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg" alt="Flutter" />
                  Flutter
                </span>
                {/* <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" alt="React Native" />
                  React Native
                </span> */}
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" alt="TypeScript" />
                  TypeScript
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg" alt="JavaScript" />
                  JavaScript
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg" alt="Java" />
                  Java
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dart/dart-original.svg" alt="Dart" />
                  Dart
                </span>
                
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg" alt="Tailwind CSS" />
                  Tailwind CSS
                </span>
              </div>
            </div>

            {/* Backend */}
            <div className="skill-category">
              <div className="skill-category-header">
                <div className="skill-icon">🖥️</div>
                <h3>Backend</h3>
              </div>
              <div className="skill-tags">
                <span className="skill-tag">
                  <img src="/icons/nestjs.svg" alt="Nestjs" />
                  Nestjs
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" alt="Node.js" />
                  Node.js
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg" alt="Express" />
                  Express
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/neovim/neovim-original.svg" alt="Neovim" />
                  Nvim
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kibana/kibana-original.svg" alt="Kibana" />
                  Kibana
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/logstash/logstash-plain.svg" alt="Logstash" />
                  Logstash
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/elasticsearch/elasticsearch-original.svg" alt="ElasticSearch" />
                  ElasticSearch
                </span>
              </div>
            </div>

            {/* Database */}
            <div className="skill-category">
              <div className="skill-category-header">
                <div className="skill-icon">🗄️</div>
                <h3>Database</h3>
              </div>
              <div className="skill-tags">
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg" alt="PostgreSQL" />
                  PostgreSQL
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" alt="MongoDB" />
                  MongoDB
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" alt="MySQL" />
                  MySQL
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg" alt="Redis" />
                  Redis
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg" alt="Firebase" />
                  Firebase
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apachekafka/apachekafka-original.svg" alt="Kafka" />
                  Kafka
                </span>
              </div>
            </div>

            {/* DevOps */}
            <div className="skill-category">
              <div className="skill-category-header">
                <div className="skill-icon">⚙️</div>
                <h3>DevOps</h3>
              </div>
              <div className="skill-tags">
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg" alt="Kubernetes" />
                  Kubernetes
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" alt="Docker" />
                  Docker
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vagrant/vagrant-original.svg" alt="Vagrant" />
                  Vagrant
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jenkins/jenkins-original.svg" alt="Jenkins" />
                  Jenkins
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg" alt="GitHub Actions" />
                  GitHub Actions
                </span>
                <span className="skill-tag">
                  <img src="/icons/aws.svg" style={{ filter: 'invert(1)' }} alt="AWS" />
                  AWS
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/prometheus/prometheus-original.svg" alt="Prometheus" />
                  Prometheus
                </span>
                <span className="skill-tag">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/grafana/grafana-original.svg" alt="Grafana" />
                  Grafana
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      {/* <section className="projects-section" id="projects" aria-label="Featured Projects">
        <div className="container">
          <h2 className="section-title">Featured Projects</h2>
          <div className="projects-grid">
            {projects.map((p, i) => (
              <article key={i} className="project-card">
                <div className="project-image">
                  { (p as any).gallery ? (
                    <ProjectGallery images={(p as any).gallery} alt={(p as any).imageAlt || p.title} />
                  ) : (
                    <img src={p.image} alt={p.title} loading="lazy" decoding="async" />
                  ) }
                </div>
                <div className="project-content">
                  <h3 className="project-title">{p.title}</h3>
                  <p className="project-description">{p.description}</p>
                  <div className="project-tech">
                    {p.tech.map((t, idx) => (
                      <span key={idx} className="tech-tag">
                        {t || "N/A"}
                      </span>
                    ))}
                  </div>
                  <div className="project-links">
                    <a
                      className="btn btn-outline"
                      href={(p as any).website || '#'}
                      target={((p as any).website ? '_blank' : undefined) as any}
                      rel={(p as any).website ? 'noopener noreferrer' : undefined}
                      aria-label={`Visit ${p.title}`}
                    >
                      View Project
                    </a>
                    { (p as any).repo ? (
                      <a
                        className="btn btn-outline"
                        href={(p as any).repo}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Source code of ${p.title}`}
                      >
                        Source Code
                      </a>
                    ) : null }
                    { (p as any).repoFrontend ? (
                      <a
                        className="btn btn-outline"
                        href={(p as any).repoFrontend}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${p.title} frontend source`}
                      >
                        Frontend Code
                      </a>
                    ) : null }
                    { (p as any).repoBackend ? (
                      <a
                        className="btn btn-outline"
                        href={(p as any).repoBackend}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${p.title} backend source`}
                      >
                        Backend Code
                      </a>
                    ) : null }
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section> */}
      <Projects />
      {/* Achievements Section */}
      <section className="achievements-section" id="achievements" aria-label="Hackathon Achievements">
        <div className="container">
          <div className="achievements-header">
            <h2 className="section-title">
              <span className="highlight">Hackathon</span> Victories
            </h2>
            <p className="achievements-description">
              Throughout my career, I've had the privilege of winning several prestigious hackathons, 
              showcasing innovative solutions to real-world problems and demonstrating technical excellence.
            </p>
          </div>
          
          <div className="achievements-grid">
            {/* Nagarjuna College Hackathon */}
            <div className="achievement-card">
              <div className="achievement-content">
                <div className="achievement-badge">🏆 1st Place</div>
                <h3 className="achievement-title">Nagarjuna College Hackathon</h3>
                <p className="achievement-description">
                  I was the sole team member and emerged victorious in my college hackathon. 
                  I built the <strong>Renit Platform</strong> - an innovative rental marketplace where users 
                  can rent anything based on their custom rental price, solving real community needs.
                </p>
                <div className="achievement-highlight">
                  <span className="highlight-label">Achievement:</span> Solo Winner
                </div>
                {/* <div className="achievement-actions">
                  <button className="achievement-btn">
                    <span>🏆</span>
                    View Certificate
                  </button>
                  <button className="achievement-btn secondary">
                    <span>📋</span>
                    Project Details
                  </button>
                </div> */}
              </div>
              <div className="achievement-image">
                <img 
                  src="/achievements/won_at_nagarjuna.jpg" 
                  alt="Nagarjuna College Hackathon Victory" 
                  onClick={() => openImagePopup("/achievements/won_at_nagarjuna.jpg", "Nagarjuna College Hackathon Victory")}
                />
              </div>
            </div>

            {/* SecurityPal NepalHacks */}
            <div className="achievement-card">
              <div className="achievement-content">
                <div className="achievement-badge">🏆 National Winner</div>
                <h3 className="achievement-title">NepalHacks at SecurityPal</h3>
                <p className="achievement-description">
                  Competed in a country-wide hackathon where only <strong>10 teams were selected</strong> from 
                  <strong>50+ teams</strong> based on idea quality and tech stack. My team won by creating a 
                  <strong>Commute-Based Traffic Prediction Platform</strong> with pattern analysis and early alert systems.
                </p>
                <div className="achievement-highlight">
                  <span className="highlight-label">Selection Rate:</span> Top 10 from 50+ teams
                </div>
                <div className="achievement-actions">
                  {/* <button className="achievement-btn">
                    <span>🏆</span>
                    View Certificate
                  </button> */}
                  {/* <button className="achievement-btn secondary">
                    <span>🚗</span>
                    Traffic Platform
                  </button> */}
                </div>
              </div>
              <div className="achievement-image">
                <img 
                  src="/achievements/won_at_securitypal.jpg" 
                  alt="NepalHacks Country Hackathon Victory" 
                  onClick={() => openImagePopup("/achievements/won_at_securitypal.jpg", "NepalHacks Country Hackathon Victory")}
                />
              </div>
            </div>

            {/* KIST All College Hackathon */}
            <div className="achievement-card">
              <div className="achievement-content">
                <div className="achievement-badge">🏆 College Champion</div>
                <h3 className="achievement-title">Kathmandu All College Hackathon</h3>
                <p className="achievement-description">
                  Competed against <strong>36 teams</strong> from colleges across Kathmandu. We developed 
                  <strong>HamroClass</strong> - a comprehensive solution addressing the traditional syllabus 
                  lag problem that affects colleges throughout Nepal.
                </p>
                <div className="achievement-highlight">
                  <span className="highlight-label">Competition:</span> 36 teams from multiple colleges
                </div>
                {/* <div className="achievement-actions">
                  <button className="achievement-btn">
                    <span>🏆</span>
                    View Certificate
                  </button>
                  <button className="achievement-btn secondary">
                    <span>🎓</span>
                    HamroClass Project
                  </button>
                </div> */}
              </div>
              <div className="achievement-image">
                <img 
                  src="/achievements/won_at_kist.jpg" 
                  alt="KIST All College Hackathon Victory" 
                  onClick={() => openImagePopup("/achievements/won_at_kist.jpg", "KIST All College Hackathon Victory")}
                />
              </div>
            </div>

            {/* Province Selection */}
            <div className="achievement-card">
              <div className="achievement-content">
                <div className="achievement-badge">🏆 Provincial Winner</div>
                <h3 className="achievement-title">Country-Wide Provincial Selection</h3>
                <p className="achievement-description">
                  Participated in a nationwide hackathon with participants from multiple provinces across the country. 
                  Our team achieved the prestigious selection from the <strong>Capital Province</strong>, 
                  representing excellence in innovation and technical implementation.
                </p>
                <div className="achievement-highlight">
                  <span className="highlight-label">Recognition:</span> Capital Province Selection
                </div>
                {/* <div className="achievement-actions">
                  <button className="achievement-btn">
                    <span>🏆</span>
                    View Certificate
                  </button>
                  <button className="achievement-btn secondary">
                    <span>🏛️</span>
                    Provincial Details
                  </button>
                </div> */}
              </div>
              <div className="achievement-image">
                <img 
                  src="/achievements/selected_from_province.jpg" 
                  alt="Capital Province Selection" 
                  onClick={() => openImagePopup("/achievements/selected_from_province.jpg", "Capital Province Selection")}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" id="contact" aria-label="Call to Action">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to work together?</h2>
            <p>Let&apos;s discuss your next project and bring your ideas to life.</p>
            {/* <a className="btn btn-primary" href="mailto:you@example.com">Get In Touch</a> */}
          </div>
        </div>
      </section>

      <ImagePopup
        image={popupImage || ''}
        alt={popupAlt}
        isOpen={!!popupImage}
        onClose={closeImagePopup}
      />
      <Connect />
    </div>
  );
};

export default Home;
